import { useCallback, useState } from 'react'

import * as snugNotifier from 'app/services/snugNotifier'
import {
  DEFAULT_MAX_FILE_SIZE_MB,
  fileToBase64,
  validatePDFFile,
} from 'app/utils/file/validation'

// Constants
const DEFAULT_CONTENT_TYPE = 'application/pdf'
const ERROR_NO_FILE = 'No file provided'
const ERROR_FILE_PROCESSING = 'File processing failed'

interface UseFileUploadOptions {
  maxSizeMB?: number
  convertToBase64?: boolean
  showNotifications?: boolean
}

interface Base64FileResult {
  name: string
  base64: string
  contentType: string
  filename: string
  pendingUpload: boolean
}

interface RawFileResult {
  name: string
  file: File
  createdAt: Date
}

type FileUploadResult = Base64FileResult | RawFileResult

interface UploadingFilesState {
  [fieldId: string]: boolean
}

interface UploadErrorsState {
  [fieldId: string]: string | null
}

type OnSuccessCallback = (fieldId: string, result: FileUploadResult) => void
type OnDeleteCallback = (fieldId: string) => void

interface UseFileUploadReturn {
  uploadingFiles: UploadingFilesState
  uploadErrors: UploadErrorsState
  handleFileUpload: (
    fieldId: string,
    file: File | null,
    onSuccess?: OnSuccessCallback,
  ) => Promise<void>
  handleFileDelete: (fieldId: string, onDelete?: OnDeleteCallback) => void
  clearAllStates: () => void
}

/**
 * Custom hook for handling file uploads with validation and base64 conversion
 * @param options - Configuration options
 * @param options.maxSizeMB - Maximum file size in megabytes (default: 10)
 * @param options.convertToBase64 - Whether to convert file to base64 (default: true)
 * @param options.showNotifications - Whether to show error notifications (default: true)
 * @returns File upload state and handlers
 */
export const useFileUpload = (
  options: UseFileUploadOptions = {},
): UseFileUploadReturn => {
  const {
    maxSizeMB = DEFAULT_MAX_FILE_SIZE_MB,
    convertToBase64 = true,
    showNotifications = true,
  } = options

  const [uploadingFiles, setUploadingFiles] = useState<UploadingFilesState>({})
  const [uploadErrors, setUploadErrors] = useState<UploadErrorsState>({})

  /**
   * Handles file upload with validation and optional base64 conversion
   * @param fieldId - The field identifier
   * @param file - The file to upload
   * @param onSuccess - Callback function called on successful upload
   * @returns Promise that resolves when upload is complete
   */
  const handleFileUpload = useCallback(
    async (
      fieldId: string,
      file: File | null,
      onSuccess?: OnSuccessCallback,
    ): Promise<void> => {
      // Set uploading state
      setUploadingFiles((prev) => ({ ...prev, [fieldId]: true }))
      setUploadErrors((prev) => ({ ...prev, [fieldId]: null }))

      // Validate file
      const validation = validatePDFFile(file, maxSizeMB)
      if (!validation.isValid) {
        setUploadErrors((prev) => ({
          ...prev,
          [fieldId]: validation.error,
        }))
        setUploadingFiles((prev) => ({ ...prev, [fieldId]: false }))
        if (showNotifications && validation.error) {
          snugNotifier.error(validation.error)
        }
        return
      }

      try {
        let result: FileUploadResult
        if (convertToBase64 && file) {
          // Convert file to base64
          const base64 = await fileToBase64(file)
          result = {
            name: file.name,
            base64,
            contentType: file.type || DEFAULT_CONTENT_TYPE,
            filename: file.name,
            pendingUpload: true,
          }
        } else if (file) {
          // Store raw file object
          result = {
            name: file.name,
            file,
            createdAt: new Date(),
          }
        } else {
          throw new Error(ERROR_NO_FILE)
        }

        // Clear uploading state
        setUploadingFiles((prev) => ({ ...prev, [fieldId]: false }))

        // Call success callback with result
        if (onSuccess) {
          onSuccess(fieldId, result)
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : ERROR_FILE_PROCESSING
        setUploadErrors((prev) => ({
          ...prev,
          [fieldId]: errorMessage,
        }))
        setUploadingFiles((prev) => ({ ...prev, [fieldId]: false }))
        if (showNotifications) {
          snugNotifier.error(errorMessage)
        }
      }
    },
    [maxSizeMB, convertToBase64, showNotifications],
  )

  /**
   * Handles file deletion
   * @param fieldId - The field identifier
   * @param onDelete - Callback function called on deletion
   */
  const handleFileDelete = useCallback(
    (fieldId: string, onDelete?: OnDeleteCallback): void => {
      setUploadErrors((prev) => ({ ...prev, [fieldId]: null }))
      if (onDelete) {
        onDelete(fieldId)
      }
    },
    [],
  )

  /**
   * Clears all upload states
   */
  const clearAllStates = useCallback((): void => {
    setUploadingFiles({})
    setUploadErrors({})
  }, [])

  return {
    uploadingFiles,
    uploadErrors,
    handleFileUpload,
    handleFileDelete,
    clearAllStates,
  }
}
